'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '../utils';

interface ExpandableHtmlProps {
  /** Pre-sanitized HTML content. The caller is responsible for sanitization
   *  (e.g., via DOMPurify) — this component renders the HTML as-is. */
  html: string;
  /** Tailwind classes that apply line clamping when collapsed.
   *  e.g. `"line-clamp-5 md:line-clamp-3"`. Static classes only — Tailwind's
   *  JIT can't resolve dynamic class names. */
  collapsedClassName: string;
  /** Class name for the inner content container (text styles, prose, etc.) */
  className?: string;
  /** Label for the "expand" toggle. Default: 'Read more' */
  expandLabel?: string;
  /** Label for the "collapse" toggle. Default: 'Show less' */
  collapseLabel?: string;
  /** Class name for the toggle button */
  buttonClassName?: string;
}

/**
 * Renders pre-sanitized HTML with a "Read more" / "Show less" toggle when
 * the content overflows the supplied clamp.
 *
 * Uses native CSS `line-clamp` so it works with any HTML structure (paragraphs,
 * inline formatting, lists, etc.) without parsing or character-level
 * truncation. Overflow detection is done with a ResizeObserver so it adapts to
 * viewport changes, font loading, and content updates.
 */
export function ExpandableHtml({
  html,
  collapsedClassName,
  className,
  expandLabel = 'Read more',
  collapseLabel = 'Show less',
  buttonClassName,
}: ExpandableHtmlProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Detect overflow whenever the content is in its collapsed state. Skips
  // measurement while expanded — `isOverflowing` retains its last value from
  // the collapsed measurement, which is what we want for the toggle button.
  useEffect(() => {
    if (isExpanded) return;
    function check() {
      const element = containerRef.current;
      if (!element) return;
      // +1 buffer to avoid sub-pixel false positives
      setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
    }
    check();
    const observer = new ResizeObserver(check);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [html, isExpanded, collapsedClassName]);

  const showButton = isExpanded || isOverflowing;

  return (
    <div>
      <div
        ref={containerRef}
        className={cn(className, !isExpanded && collapsedClassName)}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showButton && (
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          className={cn(
            'mt-1 text-sm font-medium underline-offset-2 hover:underline',
            buttonClassName,
          )}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  );
}
